import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Autocomplete,
  Button,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import "../../../components/styles/popupStyle.css";
import { removeSampleTestArray } from "../../actions/cleanup";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  fillAutocompleteValues,
  updateData,
} from "../../actions/actionItems";
import DraggableItemList from "../../subcomponents/DNDComponent";
import {
  validateString,
  validationLength,
  validationType,
} from "../../actions/validations";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { RESERVE_EQUIPMENTS_ADDON_FIELD_ADD_ON_NAME } from "../../../constants/ReserveEquipmentsAddOnsFields";
import { RESERVE_RATE_PLAN_BASE_PRICE_KEY } from "../../../constants/ReserveListScreensFields";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import AlertComponent from "../../subcomponents/AlertComponent";
import { textStyle } from "../../styles/Style";
import { ReservePropertyEnum } from "../../../constants/NavbarList";
import {
  RESERVE_ACTIVITIES_FIELD_ACTIVITY_CATEGORY,
  RESERVE_ACTIVITIES_FIELD_ACTIVITY_CODE,
  RESERVE_ACTIVITIES_FIELD_ACTIVITY_DESCRIPTION,
  RESERVE_ACTIVITIES_FIELD_ACTIVITY_IMAGE,
  RESERVE_ACTIVITIES_FIELD_ACTIVITY_NAME,
  RESERVE_ACTIVITIES_FIELD_ACTIVITY_SUB_CATEGORY,
  RESERVE_ACTIVITIES_FIELD_ACTIVITY_TYPE,
  RESERVE_ACTIVITIES_FIELD_CLASS_TYPE,
  RESERVE_ACTIVITIES_ACTIVITY_TYPE_OPTIONS,
  RESERVE_ACTIVITIES_CLASS_TYPE_OPTIONS,
  RESERVE_ACTIVITIES_FIELD_ADDONS_NAME,
  RESERVE_ACTIVITIES_FIELD_ALLOW_ADDITIONAL_GUEST_DETAILS,
  RESERVE_ACTIVITIES_FIELD_ALLOW_OVERBOOK,
  RESERVE_ACTIVITIES_FIELD_ALLOW_STANDBY,
  RESERVE_ACTIVITIES_FIELD_AUTO_CONFIRM_STANDBY,
  RESERVE_ACTIVITIES_FIELD_AVAILABLE_WEB,
  RESERVE_ACTIVITIES_FIELD_BASE_PRICE,
  RESERVE_ACTIVITIES_FIELD_CANCELLATION_DEADLINE,
  RESERVE_ACTIVITIES_FIELD_CANCELLATION_FEE_REQUIRED,
  RESERVE_ACTIVITIES_FIELD_CANCELLATION_FEE_TYPE,
  RESERVE_ACTIVITIES_FIELD_CANCELLATION_POLICY_DESCRIPTION,
  RESERVE_ACTIVITIES_FIELD_CHECK_IN_CUTOFF,
  RESERVE_ACTIVITIES_FIELD_COLLECT_ADDITIONAL_EMPLOYEE_DETAILS,
  RESERVE_ACTIVITIES_FIELD_DAYS_OF_WEEK,
  RESERVE_ACTIVITIES_FIELD_END_DATE,
  RESERVE_ACTIVITIES_FIELD_GUEST_TYPE,
  RESERVE_ACTIVITIES_FIELD_GUEST_TYPE_PRICING,
  RESERVE_ACTIVITIES_FIELD_LOCATION_NAME,
  RESERVE_ACTIVITIES_FIELD_MAX_ACTIVITY_STRENGTH,
  RESERVE_ACTIVITIES_FIELD_MAX_GROUP_SIZE,
  RESERVE_ACTIVITIES_FIELD_MAX_STANDBY_STRENGTH,
  RESERVE_ACTIVITIES_FIELD_MEMBERSHIP_PRICING,
  RESERVE_ACTIVITIES_FIELD_MEMBERSHIP_TYPE,
  RESERVE_ACTIVITIES_FIELD_MIN_ACTIVITY_STRENGTH,
  RESERVE_ACTIVITIES_FIELD_MIN_GROUP_SIZE,
  RESERVE_ACTIVITIES_FIELD_PAYMENT_TYPE,
  RESERVE_ACTIVITIES_FIELD_REQUIRE_PREPAYMENT,
  RESERVE_ACTIVITIES_FIELD_RESERVATION_PARAMETER,
  RESERVE_ACTIVITIES_FIELD_STAFF_GUIDE,
  RESERVE_ACTIVITIES_FIELD_START_DATE,
  RESERVE_ACTIVITIES_FIELD_START_END_TIME,
  RESERVE_ACTIVITIES_FIELD_START_TIME,
  RESERVE_ACTIVITIES_FIELD_END_TIME,
  RESERVE_ACTIVITIES_FIELD_NUMBER_OF_LOCATIONS,
} from "../../../constants/ReserveActivitiesFields";
import {
  reserveActivitiesTooltipActivityCategory,
  reserveActivitiesTooltipActivityCode,
  reserveActivitiesTooltipClassType,
  reserveActivitiesTooltipActivityDescription,
  reserveActivitiesTooltipActivityImage,
  reserveActivitiesTooltipActivityName,
  reserveActivitiesTooltipActivitySubCategory,
  reserveActivitiesTooltipActivityType,
  reserveActivitiesTooltipAddonsName,
  reserveActivitiesTooltipAllowAdditionalGuestDetails,
  reserveActivitiesTooltipAllowOverbookReservation,
  reserveActivitiesTooltipAllowStandbyReservation,
  reserveActivitiesTooltipAutoConfirmStandbyAfterCancellation,
  reserveActivitiesTooltipAvailableForWebReservation,
  reserveActivitiesTooltipBasePrice,
  reserveActivitiesTooltipCancellationDeadline,
  reserveActivitiesTooltipCancellationFeeRequired,
  reserveActivitiesTooltipCancellationFeeType,
  reserveActivitiesTooltipCancellationPolicyDescription,
  reserveActivitiesTooltipCheckInCutoffMinutes,
  reserveActivitiesTooltipCollectAdditionalEmployeeDetails,
  reserveActivitiesTooltipDaysOfTheWeek,
  reserveActivitiesTooltipEndDate,
  reserveActivitiesTooltipGuestType,
  reserveActivitiesTooltipGuestTypePricing,
  reserveActivitiesTooltipHeader,
  reserveListAvailableHeadingTooltip,
  reserveActivitiesTooltipLocationName,
  reserveActivitiesTooltipMaxActivityStrength,
  reserveActivitiesTooltipMaxGroupSize,
  reserveActivitiesTooltipMaxStandbyStrength,
  reserveActivitiesTooltipMembershipPricing,
  reserveActivitiesTooltipMembershipType,
  reserveActivitiesTooltipMinActivityStrength,
  reserveActivitiesTooltipMinGroupSize,
  reserveActivitiesTooltipPaymentType,
  reserveActivitiesTooltipRequirePrepayment,
  reserveActivitiesTooltipReservationParameter,
  reserveActivitiesTooltipStaffGuide,
  reserveActivitiesTooltipStartDate,
  reserveActivitiesTooltipStartTime,
  reserveActivitiesTooltipEndTime,
  reserveActivitiesTooltipNumberOfLocations,
} from "../../../constants/ReserveTooltipInformation";
import {
  converttoDayjsTime,
  converttoStandardTime,
  dayjsStartOfDayToIso,
  storedToDayjsOrNull,
  stringFromUnknown as str,
  yesNoStringFromStored,
} from "../../../constants/PropertyInformation";
import { PercentageorFlatAmount } from "../../../constants/GuaranteeField";
import {
  reserveActivitiesFieldErrors,
  reserveActivitiesFieldHelperText,
} from "../../../constants/reserveActivitiesValidationHelperText";
import { YesOrNo } from "../../../constants/YesOrNo";
import {
  formatDaysOfWeekPayload,
  parseDaysOfWeekStored,
  WEEKDAY_LABELS_MON_SUN,
} from "../../../constants/Days";
import useScrollToTop from "../../hooks/useScrollToTop";

const activityTimeFromStored = (raw: unknown): Dayjs | null => {
  const s = str(raw);
  if (!s) {
    return null;
  }
  const parsed = converttoDayjsTime(s);
  return parsed.isValid() ? parsed : null;
};

const activityTimeFromLegacyWindow = (raw: unknown): Dayjs | null => {
  const legacy = str(raw);
  if (!legacy) {
    return null;
  }
  const [startPart] = legacy.split(/\s*-\s*/).map((part) => part.trim());
  if (!startPart) {
    return null;
  }
  const parsed = dayjs(`1970-01-01 ${startPart}`);
  return parsed.isValid() ? parsed : null;
};

const activityEndTimeFromLegacyWindow = (raw: unknown): Dayjs | null => {
  const legacy = str(raw);
  if (!legacy) {
    return null;
  }
  const parts = legacy.split(/\s*-\s*/).map((part) => part.trim());
  const endPart = parts[1];
  if (!endPart) {
    return null;
  }
  const parsed = dayjs(`1970-01-01 ${endPart}`);
  return parsed.isValid() ? parsed : null;
};

const ReserveActivities = ({
  data,
  fieldValues,
}: {
  data: any;
  fieldValues: any;
}) => {
  const [activityCode, setActivityCode] = useState<string | null>(null);
  const [activityName, setActivityName] = useState<string | null>(null);
  const [activityType, setActivityType] = useState<string | null>(null);
  const [classType, setClassType] = useState<string | null>(null);
  const [activityDescription, setActivityDescription] = useState<string | null>(
    null,
  );
  const [activityCategory, setActivityCategory] = useState<string | null>(null);
  const [activitySubCategory, setActivitySubCategory] = useState<string | null>(
    null,
  );
  const [minGroupSize, setMinGroupSize] = useState<string | null>(null);
  const [maxGroupSize, setMaxGroupSize] = useState<string | null>(null);
  const [minActivityStrength, setMinActivityStrength] = useState<string | null>(
    null,
  );
  const [maxActivityStrength, setMaxActivityStrength] = useState<string | null>(
    null,
  );
  const [allowOverbookReservation, setAllowOverbookReservation] =
    useState<boolean>(false);
  const [allowStandbyReservation, setAllowStandbyReservation] =
    useState<boolean>(false);
  const [
    autoConfirmStandbyAfterCancellation,
    setAutoConfirmStandbyAfterCancellation,
  ] = useState<string | null>(null);
  const [maxStandbyStrength, setMaxStandbyStrength] = useState<string | null>(
    null,
  );
  const [guestType, setGuestType] = useState<string | null>(null);
  const [guestTypePricing, setGuestTypePricing] = useState<string | null>(null);
  const [membershipType, setMembershipType] = useState<string | null>(null);
  const [membershipPricing, setMembershipPricing] = useState<string | null>(
    null,
  );
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [selectedWeekdays, setSelectedWeekdays] = useState<string[]>([]);
  const [startTime, setStartTime] = useState<Dayjs | null>(null);
  const [endTime, setEndTime] = useState<Dayjs | null>(null);
  const [numberOfLocations, setNumberOfLocations] = useState<string | null>(
    null,
  );
  const [locationName, setLocationName] = useState<string | null>(null);
  const [staffGuide, setStaffGuide] = useState<string | null>(null);
  const [requirePrepayment, setRequirePrepayment] = useState<string | null>(
    null,
  );
  const [paymentType, setPaymentType] = useState<string | null>(null);
  const [basePrice, setBasePrice] = useState<string | null>(null);
  const [cancellationFeeRequired, setCancellationFeeRequired] = useState<
    string | null
  >(null);
  const [cancellationFeeType, setCancellationFeeType] = useState<string | null>(
    null,
  );
  const [cancellationDeadline, setCancellationDeadline] = useState<
    string | null
  >(null);
  const [cancellationPolicyDescription, setCancellationPolicyDescription] =
    useState<string | null>(null);
  const [allowAdditionalGuestDetails, setAllowAdditionalGuestDetails] =
    useState<boolean>(false);
  const [
    collectAdditionalEmployeeDetails,
    setCollectAdditionalEmployeeDetails,
  ] = useState<boolean>(false);
  const [activityImage, setActivityImage] = useState<string | null>(null);
  const [reservationParameter, setReservationParameter] = useState<
    string | null
  >(null);
  const [addonsName, setAddonsName] = useState<string | null>(null);
  const [availableForWebReservation, setAvailableForWebReservation] =
    useState<boolean>(false);
  const [checkInCutoffMinutes, setCheckInCutoffMinutes] = useState<
    string | null
  >(null);

  const [codeError, setCodeError] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [activityDescriptionErr, setActivityDescriptionErr] = useState(false);
  const [activityCategoryErr, setActivityCategoryErr] = useState(false);
  const [activitySubCategoryErr, setActivitySubCategoryErr] = useState(false);
  const [minGroupSizeErr, setMinGroupSizeErr] = useState(false);
  const [maxGroupSizeErr, setMaxGroupSizeErr] = useState(false);
  const [minActivityStrengthErr, setMinActivityStrengthErr] = useState(false);
  const [maxActivityStrengthErr, setMaxActivityStrengthErr] = useState(false);
  const [maxStandbyStrengthErr, setMaxStandbyStrengthErr] = useState(false);
  const [guestTypeErr, setGuestTypeErr] = useState(false);
  const [guestTypePricingErr, setGuestTypePricingErr] = useState(false);
  const [membershipTypeErr, setMembershipTypeErr] = useState(false);
  const [membershipPricingErr, setMembershipPricingErr] = useState(false);
  const [numberOfLocationsErr, setNumberOfLocationsErr] = useState(false);
  const [locationNameErr, setLocationNameErr] = useState(false);
  const [staffGuideErr, setStaffGuideErr] = useState(false);
  const [paymentTypeErr, setPaymentTypeErr] = useState(false);
  const [cancellationFeeRequiredErr, setCancellationFeeRequiredErr] =
    useState(false);
  const [cancellationDeadlineErr, setCancellationDeadlineErr] = useState(false);
  const [
    cancellationPolicyDescriptionErr,
    setCancellationPolicyDescriptionErr,
  ] = useState(false);
  const [activityImageErr, setActivityImageErr] = useState(false);
  const [reservationParameterErr, setReservationParameterErr] = useState(false);
  const [checkInCutoffMinutesErr, setCheckInCutoffMinutesErr] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [activities, setActivities] = useState<any[]>([]);
  const [basePriceList, setBasePriceList] = useState<any>([]);
  const [addonsNameList, setAddonsNameList] = useState<any>([]);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const { topRef, scrollToTop } = useScrollToTop();

  useEffect(() => {
    if (fieldValues) {
      const rawActivities = fieldValues[ReservePropertyEnum.activities];
      setActivities(Array.isArray(rawActivities) ? rawActivities : []);
      setBasePriceList(
        fillAutocompleteValues(
          fieldValues[ReservePropertyEnum.ratePlan],
          RESERVE_RATE_PLAN_BASE_PRICE_KEY as DNDFieldsEnum,
          "",
        ),
      );
      setAddonsNameList(
        fillAutocompleteValues(
          fieldValues[ReservePropertyEnum.equipmentsAddOns],
          RESERVE_EQUIPMENTS_ADDON_FIELD_ADD_ON_NAME as DNDFieldsEnum,
          "",
        ),
      );
    }
  }, []);

  useEffect(() => {
    data(activities);
    editClicked &&
      setSelectedId(checkData(activities, selectedId) ? selectedId : null);
  }, [activities]);

  const validateInput = () => {
    setCodeError(false);
    setNameError(false);
    setActivityDescriptionErr(false);
    setActivityCategoryErr(false);
    setActivitySubCategoryErr(false);
    setMinGroupSizeErr(false);
    setMaxGroupSizeErr(false);
    setMinActivityStrengthErr(false);
    setMaxActivityStrengthErr(false);
    setMaxStandbyStrengthErr(false);
    setGuestTypeErr(false);
    setGuestTypePricingErr(false);
    setMembershipTypeErr(false);
    setMembershipPricingErr(false);
    setNumberOfLocationsErr(false);
    setLocationNameErr(false);
    setStaffGuideErr(false);
    setPaymentTypeErr(false);
    setCancellationFeeRequiredErr(false);
    setCancellationDeadlineErr(false);
    setCancellationPolicyDescriptionErr(false);
    setActivityImageErr(false);
    setReservationParameterErr(false);
    setCheckInCutoffMinutesErr(false);

    let ok = true;
    const codeTrim = (activityCode ?? "").trim();
    const nameTrim = (activityName ?? "").trim();
    const textMax = validationLength.roomFeatureName;
    const longTextMax = validationLength.reservationGuestNotesText;

    if (!codeTrim) {
      setCodeError(true);
      ok = false;
    } else if (!validateString(codeTrim, textMax, validationType.SPACE)) {
      setCodeError(true);
      ok = false;
    }
    if (!nameTrim) {
      setNameError(true);
      ok = false;
    } else if (!validateString(nameTrim, textMax, validationType.SPACE)) {
      setNameError(true);
      ok = false;
    }

    const optionalSpace = (
      val: string | null,
      setErr: (v: boolean) => void,
    ) => {
      const t = str(val).trim();
      if (t && !validateString(t, textMax, validationType.SPACE)) {
        setErr(true);
        ok = false;
      }
    };
    const optionalName = (
      val: string | null,
      max: number,
      setErr: (v: boolean) => void,
    ) => {
      const t = str(val).trim();
      if (t && !validateString(t, max, validationType.NAME)) {
        setErr(true);
        ok = false;
      }
    };
    const optionalDigits = (
      val: string | null,
      setErr: (v: boolean) => void,
    ) => {
      const t = str(val).trim();
      if (t && !validateString(t, textMax, validationType.OTHER)) {
        setErr(true);
        ok = false;
      }
    };

    optionalName(activityDescription, longTextMax, setActivityDescriptionErr);
    optionalSpace(activityCategory, setActivityCategoryErr);
    optionalSpace(activitySubCategory, setActivitySubCategoryErr);
    optionalDigits(minGroupSize, setMinGroupSizeErr);
    optionalDigits(maxGroupSize, setMaxGroupSizeErr);
    optionalName(minActivityStrength, textMax, setMinActivityStrengthErr);
    optionalName(maxActivityStrength, textMax, setMaxActivityStrengthErr);
    optionalDigits(maxStandbyStrength, setMaxStandbyStrengthErr);
    optionalSpace(guestType, setGuestTypeErr);
    optionalName(guestTypePricing, textMax, setGuestTypePricingErr);
    optionalSpace(membershipType, setMembershipTypeErr);
    optionalName(membershipPricing, textMax, setMembershipPricingErr);
    optionalDigits(numberOfLocations, setNumberOfLocationsErr);
    optionalSpace(locationName, setLocationNameErr);
    optionalSpace(staffGuide, setStaffGuideErr);
    optionalSpace(paymentType, setPaymentTypeErr);
    optionalName(
      cancellationFeeRequired,
      longTextMax,
      setCancellationFeeRequiredErr,
    );
    optionalName(cancellationDeadline, textMax, setCancellationDeadlineErr);
    optionalName(
      cancellationPolicyDescription,
      longTextMax,
      setCancellationPolicyDescriptionErr,
    );
    optionalName(activityImage, textMax, setActivityImageErr);
    optionalName(reservationParameter, textMax, setReservationParameterErr);
    optionalDigits(checkInCutoffMinutes, setCheckInCutoffMinutesErr);

    if (!ok) {
      if (!codeTrim) {
        setAlertMessage("Activity Code is mandatory");
      } else if (!nameTrim) {
        setAlertMessage("Activity Name is mandatory");
      } else {
        setAlertMessage("Please correct the highlighted fields");
      }
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    return true;
  };

  const payload = {
    [RESERVE_ACTIVITIES_FIELD_ACTIVITY_CODE]: activityCode?.trim(),
    [RESERVE_ACTIVITIES_FIELD_ACTIVITY_NAME]: activityName?.trim(),
    [RESERVE_ACTIVITIES_FIELD_ACTIVITY_TYPE]: activityType,
    [RESERVE_ACTIVITIES_FIELD_CLASS_TYPE]: classType,
    [RESERVE_ACTIVITIES_FIELD_ACTIVITY_DESCRIPTION]: activityDescription,
    [RESERVE_ACTIVITIES_FIELD_ACTIVITY_CATEGORY]: activityCategory,
    [RESERVE_ACTIVITIES_FIELD_ACTIVITY_SUB_CATEGORY]: activitySubCategory,
    [RESERVE_ACTIVITIES_FIELD_MIN_GROUP_SIZE]: minGroupSize,
    [RESERVE_ACTIVITIES_FIELD_MAX_GROUP_SIZE]: maxGroupSize,
    [RESERVE_ACTIVITIES_FIELD_MIN_ACTIVITY_STRENGTH]: minActivityStrength,
    [RESERVE_ACTIVITIES_FIELD_MAX_ACTIVITY_STRENGTH]: maxActivityStrength,
    [RESERVE_ACTIVITIES_FIELD_ALLOW_OVERBOOK]: allowOverbookReservation
      ? YesOrNo[0]
      : YesOrNo[1],
    [RESERVE_ACTIVITIES_FIELD_ALLOW_STANDBY]: allowStandbyReservation
      ? YesOrNo[0]
      : YesOrNo[1],
    [RESERVE_ACTIVITIES_FIELD_AUTO_CONFIRM_STANDBY]:
      autoConfirmStandbyAfterCancellation,
    [RESERVE_ACTIVITIES_FIELD_MAX_STANDBY_STRENGTH]: maxStandbyStrength,
    [RESERVE_ACTIVITIES_FIELD_GUEST_TYPE]: guestType,
    [RESERVE_ACTIVITIES_FIELD_GUEST_TYPE_PRICING]: guestTypePricing,
    [RESERVE_ACTIVITIES_FIELD_MEMBERSHIP_TYPE]: membershipType,
    [RESERVE_ACTIVITIES_FIELD_MEMBERSHIP_PRICING]: membershipPricing,
    [RESERVE_ACTIVITIES_FIELD_START_DATE]: startDate,
    [RESERVE_ACTIVITIES_FIELD_END_DATE]: endDate,
    [RESERVE_ACTIVITIES_FIELD_DAYS_OF_WEEK]:
      formatDaysOfWeekPayload(selectedWeekdays),
    [RESERVE_ACTIVITIES_FIELD_START_TIME]: startTime
      ? converttoStandardTime(startTime)
      : null,
    [RESERVE_ACTIVITIES_FIELD_END_TIME]: endTime
      ? converttoStandardTime(endTime)
      : null,
    [RESERVE_ACTIVITIES_FIELD_NUMBER_OF_LOCATIONS]: numberOfLocations,
    [RESERVE_ACTIVITIES_FIELD_LOCATION_NAME]: locationName,
    [RESERVE_ACTIVITIES_FIELD_STAFF_GUIDE]: staffGuide,
    [RESERVE_ACTIVITIES_FIELD_REQUIRE_PREPAYMENT]: requirePrepayment,
    [RESERVE_ACTIVITIES_FIELD_PAYMENT_TYPE]: paymentType,
    [RESERVE_ACTIVITIES_FIELD_BASE_PRICE]: basePrice,
    [RESERVE_ACTIVITIES_FIELD_CANCELLATION_FEE_REQUIRED]:
      cancellationFeeRequired,
    [RESERVE_ACTIVITIES_FIELD_CANCELLATION_FEE_TYPE]: cancellationFeeType,
    [RESERVE_ACTIVITIES_FIELD_CANCELLATION_DEADLINE]: cancellationDeadline,
    [RESERVE_ACTIVITIES_FIELD_CANCELLATION_POLICY_DESCRIPTION]:
      cancellationPolicyDescription,
    [RESERVE_ACTIVITIES_FIELD_ALLOW_ADDITIONAL_GUEST_DETAILS]:
      allowAdditionalGuestDetails ? YesOrNo[0] : YesOrNo[1],
    [RESERVE_ACTIVITIES_FIELD_COLLECT_ADDITIONAL_EMPLOYEE_DETAILS]:
      collectAdditionalEmployeeDetails ? YesOrNo[0] : YesOrNo[1],
    [RESERVE_ACTIVITIES_FIELD_ACTIVITY_IMAGE]: activityImage,
    [RESERVE_ACTIVITIES_FIELD_RESERVATION_PARAMETER]: reservationParameter,
    [RESERVE_ACTIVITIES_FIELD_ADDONS_NAME]: addonsName,
    [RESERVE_ACTIVITIES_FIELD_AVAILABLE_WEB]: availableForWebReservation
      ? YesOrNo[0]
      : YesOrNo[1],
    [RESERVE_ACTIVITIES_FIELD_CHECK_IN_CUTOFF]: checkInCutoffMinutes,
    Code: activityCode?.trim(),
    Name: activityName?.trim(),
  };

  const updateReserveActivities = () => {
    if (!validateInput()) {
      return;
    }
    if (
      checkDuplicate(
        (activities ?? []).filter(
          (item: any) => String(item.id) !== String(selectedId),
        ),
        RESERVE_ACTIVITIES_FIELD_ACTIVITY_CODE,
        activityCode?.trim(),
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setNameError(false);
    setActivities((prev: any) => {
      return (prev ?? []).map((item: any) => {
        if (String(item.id) === String(selectedId)) {
          return {
            ...item,
            ...payload,
          };
        }
        return item;
      });
    });
    editReserveActivities(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${ReservePropertyEnum.activities} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const addReserveActivities = () => {
    if (!validateInput()) {
      return;
    }
    if (
      checkDuplicate(
        activities ?? [],
        RESERVE_ACTIVITIES_FIELD_ACTIVITY_CODE,
        activityCode?.trim(),
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setActivities((prev: any) => {
      return updateData(
        Array.isArray(prev) && prev.length
          ? [
              ...prev,
              {
                id: prev.length + 1,
                ...payload,
              },
            ]
          : [
              {
                id: 1,
                ...payload,
              },
            ],
      );
    });
    setLastUpdatedRecord(payload);
    editReserveActivities(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${ReservePropertyEnum.activities} added successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const editReserveActivities = (list: any) => {
    list?.id && setSelectedId(list.id);
    list !== null && setEditClicked(true);
    if (!list) {
      setActivityCode(null);
      setActivityName(null);
      setActivityType(null);
      setClassType(null);
      setActivityDescription(null);
      setActivityCategory(null);
      setActivitySubCategory(null);
      setMinGroupSize(null);
      setMaxGroupSize(null);
      setMinActivityStrength(null);
      setMaxActivityStrength(null);
      setAllowOverbookReservation(false);
      setAllowStandbyReservation(false);
      setAutoConfirmStandbyAfterCancellation(null);
      setMaxStandbyStrength(null);
      setGuestType(null);
      setGuestTypePricing(null);
      setMembershipType(null);
      setMembershipPricing(null);
      setStartDate(null);
      setEndDate(null);
      setSelectedWeekdays([]);
      setStartTime(null);
      setEndTime(null);
      setNumberOfLocations(null);
      setLocationName(null);
      setStaffGuide(null);
      setRequirePrepayment(null);
      setPaymentType(null);
      setBasePrice(null);
      setCancellationFeeRequired(null);
      setCancellationFeeType(null);
      setCancellationDeadline(null);
      setCancellationPolicyDescription(null);
      setAllowAdditionalGuestDetails(false);
      setCollectAdditionalEmployeeDetails(false);
      setActivityImage(null);
      setReservationParameter(null);
      setAddonsName(null);
      setAvailableForWebReservation(false);
      setCheckInCutoffMinutes(null);
      setCodeError(false);
      setNameError(false);
      return;
    }
    setActivityCode(
      str(list[RESERVE_ACTIVITIES_FIELD_ACTIVITY_CODE] ?? list["Code"]),
    );
    setActivityName(
      str(list[RESERVE_ACTIVITIES_FIELD_ACTIVITY_NAME] ?? list["Name"]),
    );
    setActivityType(str(list[RESERVE_ACTIVITIES_FIELD_ACTIVITY_TYPE]));
    setClassType(str(list[RESERVE_ACTIVITIES_FIELD_CLASS_TYPE]));
    setActivityDescription(
      str(list[RESERVE_ACTIVITIES_FIELD_ACTIVITY_DESCRIPTION]),
    );
    setActivityCategory(str(list[RESERVE_ACTIVITIES_FIELD_ACTIVITY_CATEGORY]));
    setActivitySubCategory(
      str(list[RESERVE_ACTIVITIES_FIELD_ACTIVITY_SUB_CATEGORY]),
    );
    setMinGroupSize(str(list[RESERVE_ACTIVITIES_FIELD_MIN_GROUP_SIZE]));
    setMaxGroupSize(str(list[RESERVE_ACTIVITIES_FIELD_MAX_GROUP_SIZE]));
    setMinActivityStrength(
      str(list[RESERVE_ACTIVITIES_FIELD_MIN_ACTIVITY_STRENGTH]),
    );
    setMaxActivityStrength(
      str(list[RESERVE_ACTIVITIES_FIELD_MAX_ACTIVITY_STRENGTH]),
    );
    setAllowOverbookReservation(
      yesNoStringFromStored(list[RESERVE_ACTIVITIES_FIELD_ALLOW_OVERBOOK]) ===
        YesOrNo[0],
    );
    setAllowStandbyReservation(
      yesNoStringFromStored(list[RESERVE_ACTIVITIES_FIELD_ALLOW_STANDBY]) ===
        YesOrNo[0],
    );
    setAutoConfirmStandbyAfterCancellation(
      yesNoStringFromStored(
        list[RESERVE_ACTIVITIES_FIELD_AUTO_CONFIRM_STANDBY],
      ),
    );
    setMaxStandbyStrength(
      str(list[RESERVE_ACTIVITIES_FIELD_MAX_STANDBY_STRENGTH]),
    );
    setGuestType(str(list[RESERVE_ACTIVITIES_FIELD_GUEST_TYPE]));
    setGuestTypePricing(str(list[RESERVE_ACTIVITIES_FIELD_GUEST_TYPE_PRICING]));
    setMembershipType(str(list[RESERVE_ACTIVITIES_FIELD_MEMBERSHIP_TYPE]));
    setMembershipPricing(
      str(list[RESERVE_ACTIVITIES_FIELD_MEMBERSHIP_PRICING]),
    );
    setStartDate(str(list[RESERVE_ACTIVITIES_FIELD_START_DATE]));
    setEndDate(str(list[RESERVE_ACTIVITIES_FIELD_END_DATE]));
    setSelectedWeekdays(
      parseDaysOfWeekStored(str(list[RESERVE_ACTIVITIES_FIELD_DAYS_OF_WEEK])),
    );
    setStartTime(
      activityTimeFromStored(list[RESERVE_ACTIVITIES_FIELD_START_TIME]) ??
        activityTimeFromLegacyWindow(
          list[RESERVE_ACTIVITIES_FIELD_START_END_TIME],
        ),
    );
    setEndTime(
      activityTimeFromStored(list[RESERVE_ACTIVITIES_FIELD_END_TIME]) ??
        activityEndTimeFromLegacyWindow(
          list[RESERVE_ACTIVITIES_FIELD_START_END_TIME],
        ),
    );
    setNumberOfLocations(
      str(list[RESERVE_ACTIVITIES_FIELD_NUMBER_OF_LOCATIONS]),
    );
    setLocationName(str(list[RESERVE_ACTIVITIES_FIELD_LOCATION_NAME]));
    setStaffGuide(str(list[RESERVE_ACTIVITIES_FIELD_STAFF_GUIDE]));
    setRequirePrepayment(
      yesNoStringFromStored(list[RESERVE_ACTIVITIES_FIELD_REQUIRE_PREPAYMENT]),
    );
    setPaymentType(str(list[RESERVE_ACTIVITIES_FIELD_PAYMENT_TYPE]));
    setBasePrice(str(list[RESERVE_ACTIVITIES_FIELD_BASE_PRICE]));
    setCancellationFeeRequired(
      str(list[RESERVE_ACTIVITIES_FIELD_CANCELLATION_FEE_REQUIRED]) || null,
    );
    setCancellationFeeType(
      str(list[RESERVE_ACTIVITIES_FIELD_CANCELLATION_FEE_TYPE]),
    );
    setCancellationDeadline(
      str(list[RESERVE_ACTIVITIES_FIELD_CANCELLATION_DEADLINE]),
    );
    setCancellationPolicyDescription(
      str(list[RESERVE_ACTIVITIES_FIELD_CANCELLATION_POLICY_DESCRIPTION]),
    );
    setAllowAdditionalGuestDetails(
      yesNoStringFromStored(
        list[RESERVE_ACTIVITIES_FIELD_ALLOW_ADDITIONAL_GUEST_DETAILS],
      ) === YesOrNo[0],
    );
    setCollectAdditionalEmployeeDetails(
      yesNoStringFromStored(
        list[RESERVE_ACTIVITIES_FIELD_COLLECT_ADDITIONAL_EMPLOYEE_DETAILS],
      ) === YesOrNo[0],
    );
    setActivityImage(str(list[RESERVE_ACTIVITIES_FIELD_ACTIVITY_IMAGE]));
    setReservationParameter(
      str(list[RESERVE_ACTIVITIES_FIELD_RESERVATION_PARAMETER]),
    );
    setAddonsName(str(list[RESERVE_ACTIVITIES_FIELD_ADDONS_NAME]));
    setAvailableForWebReservation(
      yesNoStringFromStored(list[RESERVE_ACTIVITIES_FIELD_AVAILABLE_WEB]) ===
        YesOrNo[0],
    );
    setCheckInCutoffMinutes(
      str(list[RESERVE_ACTIVITIES_FIELD_CHECK_IN_CUTOFF]),
    );
    setCodeError(false);
    setNameError(false);
  };

  const deleteReserveActivities = (id: number) => {
    setActivities(deleteItem(activities ?? [], id));
  };

  const rowLayout = {
    className: "form-two-col-row",
    direction: "row" as const,
    width: "100%" as const,
    gap: "20px",
    flexWrap: "wrap" as const,
  };

  const activityCodeHelperText = codeError
    ? !activityCode?.trim()
      ? reserveActivitiesFieldErrors.required
      : reserveActivitiesFieldErrors.activityCode
    : reserveActivitiesFieldHelperText.activityCode;

  const activityNameHelperText = nameError
    ? !activityName?.trim()
      ? reserveActivitiesFieldErrors.required
      : reserveActivitiesFieldErrors.activityName
    : reserveActivitiesFieldHelperText.activityName;

  const activityDescriptionHelperText = activityDescriptionErr
    ? reserveActivitiesFieldErrors.activityDescription
    : reserveActivitiesFieldHelperText.activityDescription;

  const activityCategoryHelperText = activityCategoryErr
    ? reserveActivitiesFieldErrors.activityCategory
    : reserveActivitiesFieldHelperText.activityCategory;

  const activitySubCategoryHelperText = activitySubCategoryErr
    ? reserveActivitiesFieldErrors.activitySubCategory
    : reserveActivitiesFieldHelperText.activitySubCategory;

  const minGroupSizeHelperText = minGroupSizeErr
    ? reserveActivitiesFieldErrors.minGroupSize
    : reserveActivitiesFieldHelperText.minGroupSize;

  const maxGroupSizeHelperText = maxGroupSizeErr
    ? reserveActivitiesFieldErrors.maxGroupSize
    : reserveActivitiesFieldHelperText.maxGroupSize;

  const minActivityStrengthHelperText = minActivityStrengthErr
    ? reserveActivitiesFieldErrors.minActivityStrength
    : reserveActivitiesFieldHelperText.minActivityStrength;

  const maxActivityStrengthHelperText = maxActivityStrengthErr
    ? reserveActivitiesFieldErrors.maxActivityStrength
    : reserveActivitiesFieldHelperText.maxActivityStrength;

  const maxStandbyStrengthHelperText = maxStandbyStrengthErr
    ? reserveActivitiesFieldErrors.maxStandbyStrength
    : reserveActivitiesFieldHelperText.maxStandbyStrength;

  const guestTypeHelperText = guestTypeErr
    ? reserveActivitiesFieldErrors.guestType
    : reserveActivitiesFieldHelperText.guestType;

  const guestTypePricingHelperText = guestTypePricingErr
    ? reserveActivitiesFieldErrors.guestTypePricing
    : reserveActivitiesFieldHelperText.guestTypePricing;

  const membershipTypeHelperText = membershipTypeErr
    ? reserveActivitiesFieldErrors.membershipType
    : reserveActivitiesFieldHelperText.membershipType;

  const membershipPricingHelperText = membershipPricingErr
    ? reserveActivitiesFieldErrors.membershipPricing
    : reserveActivitiesFieldHelperText.membershipPricing;

  const numberOfLocationsHelperText = numberOfLocationsErr
    ? reserveActivitiesFieldErrors.numberOfLocations
    : reserveActivitiesFieldHelperText.numberOfLocations;

  const locationNameHelperText = locationNameErr
    ? reserveActivitiesFieldErrors.locationName
    : reserveActivitiesFieldHelperText.locationName;

  const staffGuideHelperText = staffGuideErr
    ? reserveActivitiesFieldErrors.staffGuide
    : reserveActivitiesFieldHelperText.staffGuide;

  const paymentTypeHelperText = paymentTypeErr
    ? reserveActivitiesFieldErrors.paymentType
    : reserveActivitiesFieldHelperText.paymentType;

  const cancellationFeeRequiredHelperText = cancellationFeeRequiredErr
    ? reserveActivitiesFieldErrors.cancellationFeeRequired
    : reserveActivitiesFieldHelperText.cancellationFeeRequired;

  const cancellationDeadlineHelperText = cancellationDeadlineErr
    ? reserveActivitiesFieldErrors.cancellationDeadline
    : reserveActivitiesFieldHelperText.cancellationDeadline;

  const cancellationPolicyDescriptionHelperText =
    cancellationPolicyDescriptionErr
      ? reserveActivitiesFieldErrors.cancellationPolicyDescription
      : reserveActivitiesFieldHelperText.cancellationPolicyDescription;

  const activityImageHelperText = activityImageErr
    ? reserveActivitiesFieldErrors.activityImage
    : reserveActivitiesFieldHelperText.activityImage;

  const reservationParameterHelperText = reservationParameterErr
    ? reserveActivitiesFieldErrors.reservationParameter
    : reserveActivitiesFieldHelperText.reservationParameter;

  const checkInCutoffMinutesHelperText = checkInCutoffMinutesErr
    ? reserveActivitiesFieldErrors.checkInCutoffMinutes
    : reserveActivitiesFieldHelperText.checkInCutoffMinutes;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack direction="column" gap="10px">
        <div ref={topRef} />
        <Stack direction="column" width="100%">
          <Paper
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              padding: "20px",
            }}
          >
            <Stack
              direction="row"
              width="100%"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Stack
                direction="row"
                width="100%"
                justifyContent="space-between"
              >
                <Typography variant="h6" component="div" sx={textStyle}>
                  {ReservePropertyEnum.activities}
                  <Tooltip
                    title={tooltipComponent(reserveActivitiesTooltipHeader)}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </Typography>
                <Stack direction="row" gap="10px">
                  {editClicked && (
                    <ClearButton
                      click={() => {
                        editReserveActivities(null);
                        setSelectedId(null);
                      }}
                    />
                  )}
                  {lastUpdatedRecord && (
                    <CopyButton
                      click={() => {
                        editReserveActivities({
                          ...lastUpdatedRecord,
                          id: undefined,
                        });
                      }}
                    />
                  )}
                </Stack>
              </Stack>
            </Stack>

            <Stack {...rowLayout}>
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel component="legend">
                  Activity Code
                  <Tooltip
                    title={tooltipComponent(
                      reserveActivitiesTooltipActivityCode,
                    )}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <TextField
                  variant="outlined"
                  fullWidth
                  value={activityCode ?? ""}
                  onChange={(e: any) => {
                    setActivityCode(e.target.value);
                    setCodeError(false);
                  }}
                  error={codeError}
                  helperText={activityCodeHelperText}
                />
              </Stack>
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel component="legend">
                  Activity Name
                  <Tooltip
                    title={tooltipComponent(
                      reserveActivitiesTooltipActivityName,
                    )}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <TextField
                  variant="outlined"
                  fullWidth
                  value={activityName ?? ""}
                  onChange={(e: any) => {
                    setActivityName(e.target.value);
                    setNameError(false);
                  }}
                  error={nameError}
                  helperText={activityNameHelperText}
                />
              </Stack>
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel component="legend">
                  Activity Type
                  <Tooltip
                    title={tooltipComponent(
                      reserveActivitiesTooltipActivityType,
                    )}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <FormControl fullWidth size="small" variant="outlined">
                  <Select
                    displayEmpty
                    value={activityType ?? ""}
                    onChange={(e: SelectChangeEvent<string>) =>
                      setActivityType(e.target.value || null)
                    }
                    renderValue={(selected) =>
                      selected === "" ? (
                        <Typography variant="body2" color="text.secondary">
                          Select activity type
                        </Typography>
                      ) : (
                        selected
                      )
                    }
                  >
                    <MenuItem value="">
                      <em>Select activity type</em>
                    </MenuItem>
                    {RESERVE_ACTIVITIES_ACTIVITY_TYPE_OPTIONS.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel component="legend">
                  Class Type
                  <Tooltip
                    title={tooltipComponent(reserveActivitiesTooltipClassType)}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <FormControl fullWidth size="small" variant="outlined">
                  <Select
                    displayEmpty
                    value={classType ?? ""}
                    onChange={(e: SelectChangeEvent<string>) =>
                      setClassType(e.target.value || null)
                    }
                    renderValue={(selected) =>
                      selected === "" ? (
                        <Typography variant="body2" color="text.secondary">
                          Select class type
                        </Typography>
                      ) : (
                        selected
                      )
                    }
                  >
                    <MenuItem value="">
                      <em>Select class type</em>
                    </MenuItem>
                    {RESERVE_ACTIVITIES_CLASS_TYPE_OPTIONS.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Stack>

            <Stack {...rowLayout}>
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel component="legend">
                  Activity Description
                  <Tooltip
                    title={tooltipComponent(
                      reserveActivitiesTooltipActivityDescription,
                    )}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <TextField
                  variant="outlined"
                  fullWidth
                  multiline
                  minRows={3}
                  value={activityDescription ?? ""}
                  onChange={(e: any) => {
                    setActivityDescription(e.target.value);
                    setActivityDescriptionErr(false);
                  }}
                  error={activityDescriptionErr}
                  helperText={activityDescriptionHelperText}
                />
              </Stack>
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel component="legend">
                  Activity Category
                  <Tooltip
                    title={tooltipComponent(
                      reserveActivitiesTooltipActivityCategory,
                    )}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <TextField
                  variant="outlined"
                  fullWidth
                  value={activityCategory ?? ""}
                  onChange={(e: any) => {
                    setActivityCategory(e.target.value);
                    setActivityCategoryErr(false);
                  }}
                  error={activityCategoryErr}
                  helperText={activityCategoryHelperText}
                />
              </Stack>
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel component="legend">
                  Activity Sub-Category
                  <Tooltip
                    title={tooltipComponent(
                      reserveActivitiesTooltipActivitySubCategory,
                    )}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <TextField
                  variant="outlined"
                  fullWidth
                  value={activitySubCategory ?? ""}
                  onChange={(e: any) => {
                    setActivitySubCategory(e.target.value);
                    setActivitySubCategoryErr(false);
                  }}
                  error={activitySubCategoryErr}
                  helperText={activitySubCategoryHelperText}
                />
              </Stack>
            </Stack>

            <Stack {...rowLayout}>
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel component="legend">
                  Min Group Size
                  <Tooltip
                    title={tooltipComponent(
                      reserveActivitiesTooltipMinGroupSize,
                    )}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <TextField
                  variant="outlined"
                  fullWidth
                  value={minGroupSize ?? ""}
                  onChange={(e: any) => {
                    setMinGroupSize(e.target.value);
                    setMinGroupSizeErr(false);
                  }}
                  error={minGroupSizeErr}
                  helperText={minGroupSizeHelperText}
                />
              </Stack>
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel component="legend">
                  Max Group Size
                  <Tooltip
                    title={tooltipComponent(
                      reserveActivitiesTooltipMaxGroupSize,
                    )}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <TextField
                  variant="outlined"
                  fullWidth
                  value={maxGroupSize ?? ""}
                  onChange={(e: any) => {
                    setMaxGroupSize(e.target.value);
                    setMaxGroupSizeErr(false);
                  }}
                  error={maxGroupSizeErr}
                  helperText={maxGroupSizeHelperText}
                />
              </Stack>
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel component="legend">
                  Min Activity Strength
                  <Tooltip
                    title={tooltipComponent(
                      reserveActivitiesTooltipMinActivityStrength,
                    )}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <TextField
                  variant="outlined"
                  fullWidth
                  value={minActivityStrength ?? ""}
                  onChange={(e: any) => {
                    setMinActivityStrength(e.target.value);
                    setMinActivityStrengthErr(false);
                  }}
                  error={minActivityStrengthErr}
                  helperText={minActivityStrengthHelperText}
                />
              </Stack>
            </Stack>

            <Stack {...rowLayout}>
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel component="legend">
                  Max Activity Strength
                  <Tooltip
                    title={tooltipComponent(
                      reserveActivitiesTooltipMaxActivityStrength,
                    )}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <TextField
                  variant="outlined"
                  fullWidth
                  value={maxActivityStrength ?? ""}
                  onChange={(e: any) => {
                    setMaxActivityStrength(e.target.value);
                    setMaxActivityStrengthErr(false);
                  }}
                  error={maxActivityStrengthErr}
                  helperText={maxActivityStrengthHelperText}
                />
              </Stack>
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel
                  component="legend"
                  id="reserve-activities-allow-overbook"
                >
                  Allow Overbook Reservation
                  <Tooltip
                    title={tooltipComponent(
                      reserveActivitiesTooltipAllowOverbookReservation,
                    )}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <Stack
                  className="form-checkbox-row form-two-col-row"
                  direction="row"
                  alignItems="center"
                  gap={1}
                >
                  <Switch
                    checked={allowOverbookReservation}
                    onChange={() =>
                      setAllowOverbookReservation(!allowOverbookReservation)
                    }
                    inputProps={{
                      "aria-labelledby": "reserve-activities-allow-overbook",
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {allowOverbookReservation ? YesOrNo[0] : YesOrNo[1]}
                  </Typography>
                </Stack>
              </Stack>
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel
                  component="legend"
                  id="reserve-activities-allow-standby"
                >
                  Allow Standby Reservation
                  <Tooltip
                    title={tooltipComponent(
                      reserveActivitiesTooltipAllowStandbyReservation,
                    )}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <Stack
                  className="form-checkbox-row form-two-col-row"
                  direction="row"
                  alignItems="center"
                  gap={1}
                >
                  <Switch
                    checked={allowStandbyReservation}
                    onChange={() =>
                      setAllowStandbyReservation(!allowStandbyReservation)
                    }
                    inputProps={{
                      "aria-labelledby": "reserve-activities-allow-standby",
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {allowStandbyReservation ? YesOrNo[0] : YesOrNo[1]}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>

            <Stack {...rowLayout}>
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel component="legend">
                  Auto confirmation of standby after cancellation
                  <Tooltip
                    title={tooltipComponent(
                      reserveActivitiesTooltipAutoConfirmStandbyAfterCancellation,
                    )}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <FormControl fullWidth size="small" variant="outlined">
                  <Select
                    displayEmpty
                    value={autoConfirmStandbyAfterCancellation ?? ""}
                    onChange={(e: SelectChangeEvent<string>) =>
                      setAutoConfirmStandbyAfterCancellation(e.target.value)
                    }
                    renderValue={(selected) =>
                      selected === "" ? (
                        <Typography variant="body2" color="text.secondary">
                          Not set
                        </Typography>
                      ) : (
                        selected
                      )
                    }
                  >
                    {["", ...YesOrNo].map((opt) => (
                      <MenuItem key={opt || "blank"} value={opt}>
                        {opt === "" ? <em>Not set</em> : opt}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel component="legend">
                  Max Standby Strength
                  <Tooltip
                    title={tooltipComponent(
                      reserveActivitiesTooltipMaxStandbyStrength,
                    )}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <TextField
                  variant="outlined"
                  fullWidth
                  value={maxStandbyStrength ?? ""}
                  onChange={(e: any) => {
                    setMaxStandbyStrength(e.target.value);
                    setMaxStandbyStrengthErr(false);
                  }}
                  error={maxStandbyStrengthErr}
                  helperText={maxStandbyStrengthHelperText}
                />
              </Stack>
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel component="legend">
                  Guest Type
                  <Tooltip
                    title={tooltipComponent(reserveActivitiesTooltipGuestType)}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <TextField
                  variant="outlined"
                  fullWidth
                  value={guestType ?? ""}
                  onChange={(e: any) => {
                    setGuestType(e.target.value);
                    setGuestTypeErr(false);
                  }}
                  error={guestTypeErr}
                  helperText={guestTypeHelperText}
                />
              </Stack>
            </Stack>

            <Stack {...rowLayout}>
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel component="legend">
                  Guest Type Pricing
                  <Tooltip
                    title={tooltipComponent(
                      reserveActivitiesTooltipGuestTypePricing,
                    )}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <TextField
                  variant="outlined"
                  fullWidth
                  value={guestTypePricing ?? ""}
                  onChange={(e: any) => {
                    setGuestTypePricing(e.target.value);
                    setGuestTypePricingErr(false);
                  }}
                  error={guestTypePricingErr}
                  helperText={guestTypePricingHelperText}
                />
              </Stack>
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel component="legend">
                  Membership Type
                  <Tooltip
                    title={tooltipComponent(
                      reserveActivitiesTooltipMembershipType,
                    )}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <TextField
                  variant="outlined"
                  fullWidth
                  value={membershipType ?? ""}
                  onChange={(e: any) => {
                    setMembershipType(e.target.value);
                    setMembershipTypeErr(false);
                  }}
                  error={membershipTypeErr}
                  helperText={membershipTypeHelperText}
                />
              </Stack>
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel component="legend">
                  Membership Pricing
                  <Tooltip
                    title={tooltipComponent(
                      reserveActivitiesTooltipMembershipPricing,
                    )}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <TextField
                  variant="outlined"
                  fullWidth
                  value={membershipPricing ?? ""}
                  onChange={(e: any) => {
                    setMembershipPricing(e.target.value);
                    setMembershipPricingErr(false);
                  }}
                  error={membershipPricingErr}
                  helperText={membershipPricingHelperText}
                />
              </Stack>
            </Stack>

            <Stack {...rowLayout}>
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel component="legend">
                  Start Date
                  <Tooltip
                    title={tooltipComponent(reserveActivitiesTooltipStartDate)}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <DatePicker
                  value={storedToDayjsOrNull(startDate)}
                  onChange={(d) => setStartDate(dayjsStartOfDayToIso(d))}
                  slotProps={{
                    textField: { fullWidth: true, size: "small" },
                  }}
                />
              </Stack>
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel component="legend">
                  End Date
                  <Tooltip
                    title={tooltipComponent(reserveActivitiesTooltipEndDate)}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <DatePicker
                  value={storedToDayjsOrNull(endDate)}
                  onChange={(d) => setEndDate(dayjsStartOfDayToIso(d))}
                  slotProps={{
                    textField: { fullWidth: true, size: "small" },
                  }}
                />
              </Stack>
              <Stack
                direction="column"
                width="100%"
                minWidth={280}
                gap="5px"
                sx={{ flex: "1 1 100%" }}
              >
                <FormLabel component="legend">
                  Days of the week
                  <Tooltip
                    title={tooltipComponent(
                      reserveActivitiesTooltipDaysOfTheWeek,
                    )}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <ToggleButtonGroup
                  value={selectedWeekdays}
                  onChange={(_, next) => {
                    setSelectedWeekdays(Array.isArray(next) ? next : []);
                  }}
                  aria-label="Days of the week"
                  size="small"
                  sx={{ flexWrap: "wrap" }}
                >
                  {WEEKDAY_LABELS_MON_SUN.map((day) => (
                    <ToggleButton
                      key={day}
                      value={day}
                      sx={{ textTransform: "none" }}
                    >
                      {day}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Stack>
            </Stack>

            <Stack {...rowLayout}>
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel component="legend">
                  Start Time
                  <Tooltip
                    title={tooltipComponent(reserveActivitiesTooltipStartTime)}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <TimePicker
                  value={startTime}
                  onChange={(v) => setStartTime(v)}
                  slotProps={{
                    textField: { fullWidth: true, size: "small" },
                  }}
                />
              </Stack>
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel component="legend">
                  End Time
                  <Tooltip
                    title={tooltipComponent(reserveActivitiesTooltipEndTime)}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <TimePicker
                  value={endTime}
                  onChange={(v) => setEndTime(v)}
                  slotProps={{
                    textField: { fullWidth: true, size: "small" },
                  }}
                />
              </Stack>
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel component="legend">
                  Number of Locations
                  <Tooltip
                    title={tooltipComponent(
                      reserveActivitiesTooltipNumberOfLocations,
                    )}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <TextField
                  variant="outlined"
                  fullWidth
                  type="number"
                  inputProps={{ min: 0 }}
                  value={numberOfLocations ?? ""}
                  onChange={(e: any) => {
                    setNumberOfLocations(e.target.value);
                    setNumberOfLocationsErr(false);
                  }}
                  error={numberOfLocationsErr}
                  helperText={numberOfLocationsHelperText}
                />
              </Stack>
            </Stack>

            <Stack {...rowLayout}>
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel component="legend">
                  Location Name
                  <Tooltip
                    title={tooltipComponent(
                      reserveActivitiesTooltipLocationName,
                    )}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <TextField
                  variant="outlined"
                  fullWidth
                  value={locationName ?? ""}
                  onChange={(e: any) => {
                    setLocationName(e.target.value);
                    setLocationNameErr(false);
                  }}
                  error={locationNameErr}
                  helperText={locationNameHelperText}
                />
              </Stack>
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel component="legend">
                  Staff/Guide
                  <Tooltip
                    title={tooltipComponent(reserveActivitiesTooltipStaffGuide)}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <TextField
                  variant="outlined"
                  fullWidth
                  value={staffGuide ?? ""}
                  onChange={(e: any) => {
                    setStaffGuide(e.target.value);
                    setStaffGuideErr(false);
                  }}
                  error={staffGuideErr}
                  helperText={staffGuideHelperText}
                />
              </Stack>
            </Stack>

            <Stack {...rowLayout}>
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel component="legend">
                  Require prepayment
                  <Tooltip
                    title={tooltipComponent(
                      reserveActivitiesTooltipRequirePrepayment,
                    )}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <FormControl fullWidth size="small" variant="outlined">
                  <Select
                    displayEmpty
                    value={requirePrepayment ?? ""}
                    onChange={(e: SelectChangeEvent<string>) =>
                      setRequirePrepayment(e.target.value)
                    }
                    renderValue={(selected) =>
                      selected === "" ? (
                        <Typography variant="body2" color="text.secondary">
                          Not set
                        </Typography>
                      ) : (
                        selected
                      )
                    }
                  >
                    {["", ...YesOrNo].map((opt) => (
                      <MenuItem key={opt || "blank"} value={opt}>
                        {opt === "" ? <em>Not set</em> : opt}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel component="legend">
                  Payment Type
                  <Tooltip
                    title={tooltipComponent(
                      reserveActivitiesTooltipPaymentType,
                    )}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <TextField
                  variant="outlined"
                  fullWidth
                  value={paymentType ?? ""}
                  onChange={(e: any) => {
                    setPaymentType(e.target.value);
                    setPaymentTypeErr(false);
                  }}
                  error={paymentTypeErr}
                  helperText={paymentTypeHelperText}
                />
              </Stack>
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel component="legend">
                  Base Price
                  <Tooltip
                    title={tooltipComponent(reserveActivitiesTooltipBasePrice)}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <Stack direction="row" width="100%" gap="5px">
                  <Autocomplete
                    id="reserveActivitiesBasePriceList"
                    disablePortal
                    options={removeSampleTestArray(basePriceList)}
                    sx={{ width: "100%" }}
                    value={basePrice}
                    disabled={!basePriceList}
                    onChange={(_e, newValue) => {
                      setBasePrice(newValue ?? null);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Base Price"
                        variant="outlined"
                        placeholder="Base Price"
                      />
                    )}
                  />
                </Stack>
              </Stack>
            </Stack>

            <Stack {...rowLayout}>
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel component="legend">
                  Cancellation Fee Required
                  <Tooltip
                    title={tooltipComponent(
                      reserveActivitiesTooltipCancellationFeeRequired,
                    )}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <TextField
                  variant="outlined"
                  fullWidth
                  value={cancellationFeeRequired ?? ""}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setCancellationFeeRequired(e.target.value || null);
                    setCancellationFeeRequiredErr(false);
                  }}
                  placeholder="Describe or enter fee details"
                  error={cancellationFeeRequiredErr}
                  helperText={cancellationFeeRequiredHelperText}
                  inputProps={{
                    "aria-label": "Cancellation fee required",
                  }}
                />
              </Stack>
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel component="legend">
                  Cancellation Fee Type
                  <Tooltip
                    title={tooltipComponent(
                      reserveActivitiesTooltipCancellationFeeType,
                    )}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <FormControl fullWidth size="small" variant="outlined">
                  <Select
                    displayEmpty
                    value={cancellationFeeType ?? ""}
                    onChange={(e: SelectChangeEvent<string>) =>
                      setCancellationFeeType(e.target.value || null)
                    }
                    renderValue={(selected) =>
                      selected === "" ? (
                        <Typography variant="body2" color="text.secondary">
                          Select cancellation fee type
                        </Typography>
                      ) : (
                        selected
                      )
                    }
                  >
                    <MenuItem value="">
                      <em>Select cancellation fee type</em>
                    </MenuItem>
                    {PercentageorFlatAmount.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel component="legend">
                  Cancellation deadline
                  <Tooltip
                    title={tooltipComponent(
                      reserveActivitiesTooltipCancellationDeadline,
                    )}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <TextField
                  variant="outlined"
                  fullWidth
                  value={cancellationDeadline ?? ""}
                  onChange={(e: any) => {
                    setCancellationDeadline(e.target.value);
                    setCancellationDeadlineErr(false);
                  }}
                  error={cancellationDeadlineErr}
                  helperText={cancellationDeadlineHelperText}
                />
              </Stack>
            </Stack>

            <Stack {...rowLayout}>
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel component="legend">
                  Cancellation Policy Description
                  <Tooltip
                    title={tooltipComponent(
                      reserveActivitiesTooltipCancellationPolicyDescription,
                    )}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <TextField
                  variant="outlined"
                  fullWidth
                  multiline
                  minRows={3}
                  value={cancellationPolicyDescription ?? ""}
                  onChange={(e: any) => {
                    setCancellationPolicyDescription(e.target.value);
                    setCancellationPolicyDescriptionErr(false);
                  }}
                  error={cancellationPolicyDescriptionErr}
                  helperText={cancellationPolicyDescriptionHelperText}
                />
              </Stack>
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel
                  component="legend"
                  id="reserve-activities-allow-additional-guest"
                >
                  Allow Additional Guest Details
                  <Tooltip
                    title={tooltipComponent(
                      reserveActivitiesTooltipAllowAdditionalGuestDetails,
                    )}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <Stack
                  className="form-checkbox-row form-two-col-row"
                  direction="row"
                  alignItems="center"
                  gap={1}
                >
                  <Switch
                    checked={allowAdditionalGuestDetails}
                    onChange={() =>
                      setAllowAdditionalGuestDetails(
                        !allowAdditionalGuestDetails,
                      )
                    }
                    inputProps={{
                      "aria-labelledby":
                        "reserve-activities-allow-additional-guest",
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {allowAdditionalGuestDetails ? YesOrNo[0] : YesOrNo[1]}
                  </Typography>
                </Stack>
              </Stack>
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel
                  component="legend"
                  id="reserve-activities-collect-employee-details"
                >
                  Collect Additional Employee Details
                  <Tooltip
                    title={tooltipComponent(
                      reserveActivitiesTooltipCollectAdditionalEmployeeDetails,
                    )}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <Stack
                  className="form-checkbox-row form-two-col-row"
                  direction="row"
                  alignItems="center"
                  gap={1}
                >
                  <Switch
                    checked={collectAdditionalEmployeeDetails}
                    onChange={() =>
                      setCollectAdditionalEmployeeDetails(
                        !collectAdditionalEmployeeDetails,
                      )
                    }
                    inputProps={{
                      "aria-labelledby":
                        "reserve-activities-collect-employee-details",
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {collectAdditionalEmployeeDetails ? YesOrNo[0] : YesOrNo[1]}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>

            <Stack {...rowLayout}>
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel component="legend">
                  Activity Image (1920 - 1080px)
                  <Tooltip
                    title={tooltipComponent(
                      reserveActivitiesTooltipActivityImage,
                    )}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <TextField
                  variant="outlined"
                  fullWidth
                  value={activityImage ?? ""}
                  onChange={(e: any) => {
                    setActivityImage(e.target.value);
                    setActivityImageErr(false);
                  }}
                  error={activityImageErr}
                  helperText={activityImageHelperText}
                />
              </Stack>
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel component="legend">
                  Reservation Parameter (Age/Height Restriction if any)
                  <Tooltip
                    title={tooltipComponent(
                      reserveActivitiesTooltipReservationParameter,
                    )}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <TextField
                  variant="outlined"
                  fullWidth
                  value={reservationParameter ?? ""}
                  onChange={(e: any) => {
                    setReservationParameter(e.target.value);
                    setReservationParameterErr(false);
                  }}
                  error={reservationParameterErr}
                  helperText={reservationParameterHelperText}
                />
              </Stack>
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel component="legend">
                  Addons Name ( Mandatory/Optional for an activity)
                  <Tooltip
                    title={tooltipComponent(reserveActivitiesTooltipAddonsName)}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <Stack direction="row" width="100%" gap="5px">
                  <Autocomplete
                    id="reserveActivitiesAddonsNameList"
                    disablePortal
                    options={removeSampleTestArray(addonsNameList)}
                    sx={{ width: "100%" }}
                    value={addonsName}
                    disabled={!addonsNameList}
                    onChange={(_e, newValue) => {
                      setAddonsName(newValue ?? null);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Addons Name"
                        variant="outlined"
                        placeholder="Addons Name"
                      />
                    )}
                  />
                </Stack>
              </Stack>
            </Stack>

            <Stack {...rowLayout}>
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel
                  component="legend"
                  id="reserve-activities-available-web"
                >
                  Available for web Reservation
                  <Tooltip
                    title={tooltipComponent(
                      reserveActivitiesTooltipAvailableForWebReservation,
                    )}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <Stack
                  className="form-checkbox-row form-two-col-row"
                  direction="row"
                  alignItems="center"
                  gap={1}
                >
                  <Switch
                    checked={availableForWebReservation}
                    onChange={() =>
                      setAvailableForWebReservation(!availableForWebReservation)
                    }
                    inputProps={{
                      "aria-labelledby": "reserve-activities-available-web",
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {availableForWebReservation ? YesOrNo[0] : YesOrNo[1]}
                  </Typography>
                </Stack>
              </Stack>
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel component="legend">
                  Check-In Cut off minutes
                  <Tooltip
                    title={tooltipComponent(
                      reserveActivitiesTooltipCheckInCutoffMinutes,
                    )}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <TextField
                  variant="outlined"
                  fullWidth
                  value={checkInCutoffMinutes ?? ""}
                  onChange={(e: any) => {
                    setCheckInCutoffMinutes(e.target.value);
                    setCheckInCutoffMinutesErr(false);
                  }}
                  error={checkInCutoffMinutesErr}
                  helperText={checkInCutoffMinutesHelperText}
                />
              </Stack>
            </Stack>

            <Stack
              direction="row"
              width="100%"
              justifyContent="flex-end"
              mt={1}
            >
              <Button
                variant="contained"
                sx={{
                  cursor: "pointer",
                  border: "1px solid gray",
                  borderRadius: "10px",
                  background: "darkgreen",
                  color: "white",
                }}
                onClick={() =>
                  selectedId
                    ? updateReserveActivities()
                    : addReserveActivities()
                }
              >
                {selectedId ? buttonValues.UPDATE : buttonValues.ADD}{" "}
                {ReservePropertyEnum.activities}
              </Button>
            </Stack>

            <Stack direction="column" gap="10px" mt={3}>
              <Typography
                variant="body2"
                sx={{ color: "red", fontWeight: 800 }}
              >
                Do not use sample data. Those are for demo purposes only!
              </Typography>
              <Typography
                variant="h6"
                component="div"
                sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
              >
                {ReservePropertyEnum.activities} available
                <Tooltip
                  title={tooltipComponent(reserveListAvailableHeadingTooltip)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Typography>
              <Stack direction="column" gap="10px">
                {Array.isArray(activities) && activities.length > 0 ? (
                  <DraggableItemList
                    itemData={activities}
                    onOrderUpdate={(order) => setActivities(order)}
                    onEdit={(info) => {
                      editReserveActivities(info);
                      scrollToTop();
                    }}
                    onDelete={(id) => deleteReserveActivities(id)}
                    code="Code"
                    name="Name"
                  />
                ) : (
                  <Typography variant="body2">
                    No {ReservePropertyEnum.activities} information available
                  </Typography>
                )}
              </Stack>
            </Stack>
          </Paper>
        </Stack>
        <AlertComponent
          message={alertMessage}
          type={alertType}
          alertOpen={openAlert}
          closeAlert={() => setOpenAlert(false)}
        />
      </Stack>
    </LocalizationProvider>
  );
};

export default ReserveActivities;
